import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, Trash2 } from 'lucide-react'
import { AppLayout } from '../../components/layout/AppLayout'
import { Chip } from '../../components/ui/Chip'
import { Button } from '../../components/ui/Button'
import { apiFetch } from '../../lib/api-client'

interface Contract {
  id: string
  lease_id: string
  type: string
  monthly_rent_oere: number
  start_date: string
  end_date: string | null
  status: string
  serial_number: number | null
}

interface Lease {
  id: string
  property_id: string
  unit_no: string
  serial_number: number | null
}

interface Property {
  id: string
  address: string
  serial_label: string
}

function statusVariant(s: string): 'green' | 'blue' | 'amber' | 'gray' | 'purple' | 'red' {
  switch (s) {
    case 'active': return 'green'
    case 'signed': return 'blue'
    case 'sent': return 'purple'
    case 'draft': return 'gray'
    case 'terminated': return 'red'
    default: return 'gray'
  }
}

function statusLabel(s: string): string {
  const map: Record<string, string> = {
    draft: 'Kladde', sent: 'Sendt', signed: 'Underskrevet',
    active: 'Aktiv', terminated: 'Opsagt',
  }
  return map[s] ?? s
}

function fmtRent(oere: number): string {
  return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', maximumFractionDigits: 0 }).format(oere / 100)
}

function fmtDate(d: string): string {
  return new Date(d).toLocaleDateString('da-DK', { year: 'numeric', month: 'short', day: 'numeric' })
}

export function LejeforholdListPage() {
  const navigate = useNavigate()
  const [contracts, setContracts] = React.useState<Contract[]>([])
  const [leasesById, setLeasesById] = React.useState<Record<string, Lease>>({})
  const [propertiesById, setPropertiesById] = React.useState<Record<string, Property>>({})
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  async function handleDelete(id: string) {
    if (!window.confirm('Slet lejeforhold? Dette kan ikke fortrydes.')) return
    try {
      await apiFetch(`/api/v1/contracts/${id}`, { method: 'DELETE' })
      setContracts((prev) => prev.filter((c) => c.id !== id))
    } catch {
      alert('Kunne ikke slette lejeforholdet.')
    }
  }

  React.useEffect(() => {
    Promise.all([
      apiFetch<Contract[]>('/api/v1/contracts'),
      apiFetch<Lease[]>('/api/v1/leases'),
      apiFetch<{ items: Property[] }>('/api/v1/properties'),
    ])
      .then(([contractsData, leasesData, propertiesData]) => {
        setContracts(contractsData)
        const byLeaseId: Record<string, Lease> = {}
        for (const l of leasesData) byLeaseId[l.id] = l
        setLeasesById(byLeaseId)
        const byPropId: Record<string, Property> = {}
        for (const p of propertiesData.items) byPropId[p.id] = p
        setPropertiesById(byPropId)
      })
      .catch(() => setError('Kunne ikke hente lejeforhold. Prøv igen.'))
      .finally(() => setLoading(false))
  }, [])

  return (
    <AppLayout
      actions={
        <Link to="/kontrakter/opret">
          <Button size="sm">
            <Plus size={13} style={{ marginRight: 4 }} />
            Opret lejeforhold
          </Button>
        </Link>
      }
    >
      <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <div style={{
          padding: '16px 24px 12px',
          borderBottom: '1px solid #e8eaed',
          fontWeight: 600,
          fontSize: 14,
          color: '#111827',
        }}>
          Lejeforhold {!loading && <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 13 }}>({contracts.length})</span>}
        </div>

        {loading && (
          <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Henter…</div>
        )}
        {error && (
          <div style={{ padding: 20, color: '#dc2626', fontSize: 13 }}>{error}</div>
        )}

        {!loading && !error && contracts.length === 0 && (
          <div style={{ padding: 56, textAlign: 'center' }}>
            <p style={{ color: '#9ca3af', fontSize: 13, marginBottom: 16 }}>
              Ingen lejeforhold oprettet endnu.
            </p>
            <Link to="/kontrakter/opret">
              <Button size="sm">
                <Plus size={13} style={{ marginRight: 4 }} />
                Opret lejeforhold
              </Button>
            </Link>
          </div>
        )}

        {!loading && !error && contracts.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {['Løbenr.', 'Lejemål', 'Type', 'Indflytning', 'Månedsleje', 'Status'].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: '10px 24px',
                      textAlign: 'left',
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e8eaed',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {contracts.map((c, i) => {
                const lease = leasesById[c.lease_id]
                const property = lease ? propertiesById[lease.property_id] : undefined
                const leaseSerial = lease?.serial_number != null
                  ? String(lease.serial_number).padStart(3, '0')
                  : '—'
                const contractSerial = c.serial_number != null
                  ? String(c.serial_number).padStart(3, '0')
                  : '—'
                const löbenr = property
                  ? `${property.serial_label}-${leaseSerial}-${contractSerial}`
                  : contractSerial

                return (
                  <LejeforholdRow
                    key={c.id}
                    contract={c}
                    lejemal={lease ? `${property?.address ?? '—'} · ${lease.unit_no}` : '—'}
                    löbenr={löbenr}
                    isLast={i === contracts.length - 1}
                    onClick={() =>
                      c.status === 'draft'
                        ? navigate(`/kontrakter/opret?draft=${c.id}`)
                        : navigate(`/lejeforhold/${c.id}`)
                    }
                    onDelete={() => handleDelete(c.id)}
                  />
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </AppLayout>
  )
}

function LejeforholdRow({
  contract: c,
  lejemal,
  löbenr,
  isLast,
  onClick,
  onDelete,
}: {
  contract: Contract
  lejemal: string
  löbenr: string
  isLast: boolean
  onClick: () => void
  onDelete: () => void
}) {
  const [hovered, setHovered] = React.useState(false)

  return (
    <tr
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? '#f9fafb' : 'transparent',
        transition: 'background 0.1s',
        borderBottom: isLast ? 'none' : '1px solid #f3f4f6',
        cursor: 'pointer',
      }}
    >
      <td style={{ padding: '12px 24px' }}>
        <span style={{
          fontFamily: 'monospace', fontSize: 12, fontWeight: 600,
          color: '#4f46e5', background: '#eff6ff',
          padding: '2px 6px', borderRadius: 4,
        }}>
          {löbenr}
        </span>
      </td>
      <td style={{ padding: '12px 24px', color: '#111827', fontWeight: 500 }}>{lejemal}</td>
      <td style={{ padding: '12px 24px', color: '#6b7280', textTransform: 'uppercase', fontSize: 11 }}>
        {c.type}
      </td>
      <td style={{ padding: '12px 24px', color: '#6b7280' }}>{fmtDate(c.start_date)}</td>
      <td style={{ padding: '12px 24px', color: '#111827', fontWeight: 500 }}>
        {fmtRent(c.monthly_rent_oere)}
      </td>
      <td style={{ padding: '12px 24px' }}>
        <Chip variant={statusVariant(c.status)}>{statusLabel(c.status)}</Chip>
      </td>
      <td style={{ padding: '12px 24px', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
        {(c.status === 'draft' || c.status === 'sent') && (
          <button
            onClick={onDelete}
            title="Slet lejeforhold"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#9ca3af', padding: 4 }}
          >
            <Trash2 size={15} />
          </button>
        )}
      </td>
    </tr>
  )
}
